import React, { FC, useCallback, useEffect, useMemo, useState, ReactNode, useRef } from 'react';
import axios, { AxiosResponse } from 'axios';
import { Empty, Spin } from 'antd';
import { OptionProps, SelectProps, SelectValue, LabeledValue } from 'antd/lib/select';
import url from 'url';
import qs from 'query-string';
import * as R from 'ramda';

import { Tooltip } from 'ui/components/Tooltip';
import { Label } from 'ui/components/Input/styled';
import { Highlighter } from 'ui/components/Highlighter';

import { NotFoundContent, TagPlaceholder } from './components';

import { useQuery } from 'hooks';

import { setFiltersToLocalStorage, getDataFromLocalStorage } from '../../helpers/localStorage';

import { FetchParamsType, cachedRequest } from './utils';

import { OptionType, DataReq, DataReqItem, SamoletSelectProps } from './types';

import './dropDownStyle.css';
import { Select as StyledSelect, Action, EmptyWrapper, Button } from './styled';

const trailingSlash = (str: string) => (str.endsWith('/') ? str : str + '/');

export const Select: FC<SamoletSelectProps> = props => {
  const {
    onCreateNewOption,
    createNewOptionText,
    onSearch,
    onBlur,
    apiUrl,
    applySingle = false,
    additionalField,
    onAdditionalFieldClick,
    options: propsOptions,
    value: propsValue,
    customOptions,
    customActions = [],
    name = '',
    renderOption,
    loadOnOpen = false,
    loadOption = false,
    loadOptions = false,
    loadOnEveryOpen = false,
    loadOnBlur = true,
    localStorageKey,
    onLoaded,
    onFirstLoad,
    labelInValue,
    selectAll,
    highlightSearch = false,
    valueProp = 'id',
    valueToString = false,
    resetIfPreloadEmpty = false,
    onManualSetValue,
    label,
    useCache = false,
    invalidateCacheTime,
    maxCacheLength,
    maxTagPlaceholderGetLabelText,
    notFoundContentProps,
    fullWidth = false,
    hasError = false,
    ...otherProps
  } = props;

  const [searchValue, setSearchValue] = useState('');
  const [currentValue, setCurrentValue] = useState<SelectValue | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchSearchLoading, setIsFetchSearchLoading] = useState(false);
  const [options, setOptions] = useState<OptionType[]>(
    customOptions || (propsOptions as OptionType[]) || [],
  );
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const { query, pushQuery } = useQuery();
  const hasSearchValue = useRef('');

  const initialLocalStorageData: Record<string, OptionType> | null = getDataFromLocalStorage(
    localStorageKey || '',
  );

  const optionsIds = useMemo(
    () => options.map(({ value }) => (valueToString ? String(value) : value)),
    [options, valueToString],
  );

  const isSelectAllBtnVisible = useMemo(
    () => props.mode === 'multiple' && selectAll && !R.equals(currentValue, optionsIds),
    [props.mode, selectAll, currentValue, optionsIds],
  );

  useEffect(() => {
    if (!localStorageKey || !initialLocalStorageData) return;

    const localStorageArrayKeys = Object.keys(initialLocalStorageData);
    const savedSelectsValuesObject = localStorageArrayKeys.reduce(
      (acc, el) => ({ ...acc, [el]: initialLocalStorageData[el].value }),
      {},
    );

    pushQuery({
      ...savedSelectsValuesObject,
      ...query,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorageKey]);

  useEffect(() => {
    if (customOptions || propsOptions) {
      setOptions(customOptions || (propsOptions as OptionType[]));
    }
  }, [customOptions, propsOptions]);

  const fetchOptions = useCallback(
    async (params: FetchParamsType) => {
      if (!apiUrl) return [];

      let data: DataReq<typeof valueProp>;

      if (useCache) {
        const resp = await cachedRequest(
          axios.get,
          apiUrl,
          params,
          invalidateCacheTime,
          maxCacheLength,
        );

        if (!resp) {
          return [];
        }

        data = resp.data;
      } else {
        // TODO Подставляет search в запрос зачем-то
        // console.log('params', params)
        // const isParamsEmpty = 'search' in params && params.search === '' || params.search === undefined
        const resp = await axios.get(apiUrl, { params });
        data = resp.data;
      }

      return (
        data.results?.map(option => ({
          ...option,
          label: renderOption ? renderOption(option) : option.name,
          value: option[valueProp],
        })) || []
      );
    },
    [apiUrl, useCache, invalidateCacheTime, maxCacheLength, renderOption, valueProp],
  );

  const fetchData = useCallback(
    async (search?: string) => {
      let fetchedData: OptionType[] = [];

      if (!apiUrl) {
        !onSearch && console.error('apiUrl is undefined');
        return;
      }

      try {
        setIsLoading(true);
        fetchedData = await fetchOptions({ search });

        // load option by selected value if not in fetched list
        if (loadOption || loadOptions) {
          let idToLoad: SelectValue = [];

          if (labelInValue) {
            if (query[name]) {
              idToLoad = query[name] || [];
            }
            if (props.defaultValue) {
              idToLoad = (props.defaultValue as LabeledValue).value;
            }
            if (propsValue) {
              idToLoad = (propsValue as LabeledValue).value;
            }
          } else {
            idToLoad = propsValue || props.defaultValue || query[name] || [];
          }

          if (loadOptions) {
            const idsToLoad = idToLoad && !Array.isArray(idToLoad) ? [idToLoad] : idToLoad || [];
            const loadedIds = fetchedData.map(item => String(item.value));
            const ids = (idsToLoad as SelectValue[]).filter(id => !loadedIds.includes(String(id)));
            const data = ids.length ? await fetchOptions({ ids: String(ids) }) : [];

            if (resetIfPreloadEmpty && ids.length && !data.length) {
              setCurrentValue(undefined);
              onManualSetValue && onManualSetValue(undefined);
            }

            fetchedData.push(...data);
          } else if (
            idToLoad &&
            !Array.isArray(idToLoad) && // idToLoad is simple value
            !fetchedData.find(item => String(item.value) === String(idToLoad))
          ) {
            const parsedUrl = qs.parseUrl(apiUrl);
            const itemUrl = url.format({
              pathname: `${trailingSlash(parsedUrl.url)}${idToLoad}`,
              query: parsedUrl.query,
            });
            const { data }: AxiosResponse<DataReqItem<typeof valueProp>> = await axios.get(itemUrl);

            fetchedData.push({
              ...data,
              label: renderOption ? renderOption(data) : data.name,
              value: data[valueProp],
            });
          }
        }

        setOptions(fetchedData);
      } catch (error) {
        console.error('Error from Select: ', error);
      } finally {
        setIsLoading(false);
        onLoaded && onLoaded(fetchedData);
      }

      return fetchedData;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      apiUrl,
      onSearch,
      name,
      query,
      loadOption,
      renderOption,
      labelInValue,
      props.defaultValue,
      propsValue,
      onLoaded,
      loadOptions,
      fetchOptions,
    ],
  );

  // Init fetch data
  useEffect(() => {
    if (loadOnOpen) return;

    async function initFetch() {
      if (apiUrl) {
        const fetchedData = await fetchData();
        const optionsValues = fetchedData?.map(option => String(option.value)) || [];
        onFirstLoad && onFirstLoad(optionsValues);
      }
    }

    initFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, onFirstLoad]);

  useEffect(() => {
    setCurrentValue(propsValue);
  }, [propsValue]);

  useEffect(() => {
    if (applySingle && options.length === 1) {
      const [{ value }] = options;

      setCurrentValue(props.mode === 'multiple' ? [String(value)] : String(value));
    }
  }, [searchValue, applySingle, props.mode, options]);

  const notFoundContent = isLoading ? (
    <Spin size='small' />
  ) : onCreateNewOption ? (
    <NotFoundContent
      createNewOptionText={createNewOptionText}
      search={searchValue}
      onClick={onCreateNewOption}
    />
  ) : (
    <EmptyWrapper>
      <Empty description={notFoundContentProps?.emptyDescription || 'Ничего не найдено'} />
    </EmptyWrapper>
  );

  const handleSetSearchValue = useCallback((search: string) => {
    setSearchValue(search);
    hasSearchValue.current = search;
  }, []);

  // - Start - Последовательные рекурсивные запросы поиска
  const handleFetchSearch = useCallback(
    async (searchText: string) => {
      setIsFetchSearchLoading(true);
      await fetchData(searchText).then(async () => {
        if (searchText === hasSearchValue.current) {
          setIsFetchSearchLoading(false);
          return;
        }
        await handleFetchSearch(hasSearchValue.current);
      });
    },
    [fetchData],
  );

  useEffect(() => {
    if (isFetchSearchLoading) return;
    handleFetchSearch(searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);
  // - End -

  const handleSearch = useCallback(
    (search: string) => {
      handleSetSearchValue(search);
      onSearch && onSearch(search);
    },
    [handleSetSearchValue, onSearch],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLElement>) => {
      handleSetSearchValue('');
      loadOnBlur && fetchData();
      onBlur && onBlur(e);
    },
    [handleSetSearchValue, loadOnBlur, fetchData, onBlur],
  );

  const handleChange: SelectProps<SelectValue>['onChange'] = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (value: any, options: any) => {
      setCurrentValue(value);

      if (localStorageKey) {
        setFiltersToLocalStorage<Omit<SelectValue, 'children'>>(localStorageKey, name, options);
      }

      props.onChange && props.onChange(value, options);
    },
    [localStorageKey, name, props],
  );

  const handleClear = useCallback(() => handleChange([], []), [handleChange]);

  const maxTagPlaceholder = useCallback(
    selectedValues => (
      <TagPlaceholder
        count={selectedValues.length}
        getLabelText={maxTagPlaceholderGetLabelText}
        onClose={handleClear}
      />
    ),
    [handleClear, maxTagPlaceholderGetLabelText],
  );

  const handleSelect = useCallback(
    (value: SelectValue, option: OptionProps) => {
      if (value === additionalField?.name && onAdditionalFieldClick) {
        onAdditionalFieldClick();
      }

      if (apiUrl && searchValue) {
        fetchData(props.mode === 'multiple' ? searchValue : undefined);
      }
      if (props.mode !== 'multiple') setSearchValue('');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const selectedValue = options.find((el: any) => el[valueProp] == option.value) as any; // TODO: remove any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      props.onSelect && props.onSelect(value as any, option, selectedValue);
    },
    [
      additionalField?.name,
      onAdditionalFieldClick,
      apiUrl,
      searchValue,
      fetchData,
      options,
      props,
      valueProp,
    ],
  );

  const handleOpen = useCallback(
    (open: boolean) => {
      if (open && apiUrl && ((loadOnOpen && !options.length) || loadOnEveryOpen)) {
        fetchData();
      }

      setDropdownVisible(open);
      props.onDropdownVisibleChange && props.onDropdownVisibleChange(open);
    },
    [apiUrl, loadOnOpen, options.length, loadOnEveryOpen, props, fetchData],
  );

  const handleSelectAll = useCallback(() => {
    handleChange(optionsIds, options);
  }, [options, handleChange, optionsIds]);

  return (
    <>
      {label && <Label>{label}</Label>}

      <StyledSelect
        value={currentValue}
        notFoundContent={notFoundContent}
        maxTagCount={0}
        maxTagPlaceholder={maxTagPlaceholder}
        filterOption={false}
        optionFilterProp='label'
        popupClassName='dropDownRender'
        loading={isLoading || isFetchSearchLoading}
        getPopupContainer={(trigger: HTMLElement) => trigger.parentElement || document.body}
        {...otherProps}
        dropdownRender={(menu: ReactNode) => {
          return (
            <div>
              {additionalField && onAdditionalFieldClick && (
                <Action key={additionalField.name} onClick={onAdditionalFieldClick}>
                  {additionalField.label}
                </Action>
              )}

              {customActions.map(action => (
                <Tooltip
                  key={action.name}
                  title={action.tooltip?.title}
                  placement={action.tooltip?.placement}
                >
                  <Action onClick={action.onClick}>{action.label}</Action>
                </Tooltip>
              ))}

              {menu}

              {isSelectAllBtnVisible && (
                <Button type='primary' onClick={handleSelectAll}>
                  Выбрать всё
                </Button>
              )}
            </div>
          );
        }}
        open={Boolean(dropdownVisible)}
        labelInValue={labelInValue}
        fullWidth={fullWidth}
        $hasError={hasError}
        onChange={handleChange}
        onSearch={props.showSearch && handleSearch}
        onSelect={handleSelect}
        onBlur={handleBlur}
        onDropdownVisibleChange={handleOpen}
      >
        {!isFetchSearchLoading && (
          <>
            {options.length && additionalField && !onAdditionalFieldClick && (
              <StyledSelect.Option key={additionalField.name} label={additionalField.label}>
                {additionalField.label}
              </StyledSelect.Option>
            )}

            {options.map(d => (
              <StyledSelect.Option
                key={String(d.value)}
                label={d.label}
                value={valueToString ? undefined : d.value}
                disabled={d?.disabled}
              >
                {highlightSearch ? (
                  <Highlighter optionLabel={d.label} searchValue={searchValue.split(' ')} />
                ) : (
                  d.label
                )}
              </StyledSelect.Option>
            ))}
          </>
        )}
      </StyledSelect>
    </>
  );
};

Select.defaultProps = {
  placeholder: 'Выберите',
};
